import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "hidden", "results", "selected"]
  static values = { url: String, minLength: { type: Number, default: 2 } }

  declare inputTarget: HTMLInputElement
  declare hiddenTarget: HTMLInputElement
  declare resultsTarget: HTMLElement
  declare selectedTarget: HTMLElement
  declare urlValue: string
  declare minLengthValue: number

  private timeout: ReturnType<typeof setTimeout> | null = null

  connect() {
    this.resultsTarget.classList.add("hidden")
    document.addEventListener("click", this.handleOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.handleOutsideClick)
  }

  search() {
    if (this.timeout) clearTimeout(this.timeout)

    const query = this.inputTarget.value.trim()
    if (query.length < this.minLengthValue) {
      this.resultsTarget.classList.add("hidden")
      return
    }

    this.timeout = setTimeout(() => this.fetchResults(query), 300)
  }

  async fetchResults(query: string) {
    const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
    const response = await fetch(url, {
      headers: { "Accept": "application/json" }
    })

    if (!response.ok) return

    const items: { id: string; name: string }[] = await response.json()
    this.renderResults(items)
  }

  renderResults(items: { id: string; name: string }[]) {
    if (items.length === 0) {
      this.resultsTarget.innerHTML = '<div class="px-4 py-2 text-sm text-gray-500">No results found</div>'
    } else {
      this.resultsTarget.innerHTML = items
        .map(item => `<div class="px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer" data-action="click->autocomplete#select" data-id="${item.id}" data-name="${item.name}">${item.name}</div>`)
        .join("")
    }
    this.resultsTarget.classList.remove("hidden")
  }

  select(event: Event) {
    const target = event.currentTarget as HTMLElement
    const id = target.dataset.id || ""
    const name = target.dataset.name || ""

    this.hiddenTarget.value = id
    this.inputTarget.value = name
    this.resultsTarget.classList.add("hidden")
    this.selectedTarget.innerHTML = `<span class="text-sm text-gray-700">${name}</span> <button type="button" data-action="click->autocomplete#clear" class="text-xs text-red-500 hover:text-red-700 ml-2">Clear</button>`
    this.selectedTarget.classList.remove("hidden")
  }

  clear() {
    this.hiddenTarget.value = ""
    this.inputTarget.value = ""
    this.selectedTarget.classList.add("hidden")
    this.selectedTarget.innerHTML = ""
  }

  handleOutsideClick = (event: Event) => {
    if (!this.element.contains(event.target as Node)) {
      this.resultsTarget.classList.add("hidden")
    }
  }
}
